import { useTranslation } from "react-i18next";
import { useDistrictManager } from "@/api/reseller";
import { Badge } from "@/components/ui/badge";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

type DistrictManagerSheetProps = {
	managerId: number | null;
	onClose: () => void;
};

export function DistrictManagerSheet({
	managerId,
	onClose,
}: DistrictManagerSheetProps) {
	const { t } = useTranslation();
	const { data, isLoading } = useDistrictManager(managerId ?? 0);

	return (
		<Sheet
			open={managerId !== null}
			onOpenChange={(open) => !open && onClose()}
		>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>{t("managers.sheetTitle")}</SheetTitle>
					<SheetDescription>{t("managers.sheetDescription")}</SheetDescription>
				</SheetHeader>

				<div className="flex flex-col gap-4 px-4 pb-4">
					{isLoading ? (
						<>
							<Skeleton className="h-4 w-3/4" />
							<Skeleton className="h-4 w-1/2" />
							<Skeleton className="h-4 w-2/3" />
						</>
					) : data ? (
						<>
							<Row
								label={t("managers.sheetCompany")}
								value={data.company_name || "—"}
							/>
							<div className="flex flex-col gap-2">
								<span className="text-muted-foreground text-sm">
									{t("managers.sheetEmails")}
								</span>
								{data.emails.length > 0 ? (
									<div className="flex flex-col gap-1">
										{data.emails.map((email) => (
											<span key={email} className="text-sm font-medium">
												{email}
											</span>
										))}
									</div>
								) : (
									<span className="text-sm font-medium">—</span>
								)}
							</div>
							<Row
								label={t("managers.sheetDistricts")}
								value={String(data.districts_count)}
							/>
							<Row
								label={t("managers.sheetUsersCount")}
								value={String(data.users_count)}
							/>
							<Row
								label={t("managers.sheetTenantsCount")}
								value={String(data.tenants_count)}
							/>
							<div className="flex items-center justify-between gap-4">
								<span className="text-muted-foreground text-sm">
									{t("managers.sheetAccountStatus")}
								</span>
								<Badge variant={data.is_confirmed ? "default" : "outline"}>
									{data.is_confirmed
										? t("managers.sheetConfirmed")
										: t("managers.sheetUnconfirmed")}
								</Badge>
							</div>
						</>
					) : null}
				</div>
			</SheetContent>
		</Sheet>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-muted-foreground text-sm">{label}</span>
			<span className="text-sm font-medium">{value}</span>
		</div>
	);
}
